import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { DOCUMENTS_LIST_QUERY_KEY } from '@/modules/admin-dashboard/queries/documents.ts';
import { CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY } from '@/modules/admin-dashboard/queries/tasks.ts';
import {
  CreateFolderRequest,
  CreatePublicFolderLinkV1DocumentFoldersFolderIdPublicLinkPostParams,
  DeleteFolderV1DocumentFoldersFolderIdDeleteParams,
  DeletePublicFolderLinkV1DocumentFoldersFolderIdPublicLinkDeleteParams,
  GetDocumentTreeV1DocumentFoldersTreeGetParams,
  GetPublicDocumentFolderTreeV1UserDocumentsFoldersPublicTreeGetParams,
  GetPublicDocumentV1UserDocumentsPublicPublicDocumentIdGetParams,
  GetPublicFolderDocumentsV1UserDocumentsPublicFoldersDocumentsGetParams,
  MoveDocumentRequest,
  MoveFolderRequest,
  UpdateFolderRequest,
} from 'compliance-api';

export const DOCUMENTS_TREE_QK = 'vestra-compliance/documents-folder-tree';

export const useCreateFolderMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateFolderRequest) => {
      const response = await withErrorHandling(api.v1.createFolderV1DocumentFoldersPost(payload), {
        loading: 'Creating folder...',
        success: 'Folder created',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useFoldersTreeQuery = (
  payload: GetDocumentTreeV1DocumentFoldersTreeGetParams,
  disabled = false,
) => {
  return useQuery({
    queryKey: [DOCUMENTS_TREE_QK, JSON.stringify(payload), useGetCurrentBusinessId()],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getDocumentFolderTreeV1UserDocumentsFoldersTreeGet(payload);
      return response.data;
    },
    retry: 2,
    enabled: !disabled,
  });
};

export const useMoveFolderMutation = () => {
  return useMutation({
    mutationFn: async (payload: MoveFolderRequest) => {
      const response = await withErrorHandling(
        api.v1.moveFolderV1DocumentFoldersMovePatch(payload),
        {
          loading: 'Moving folder...',
          success: 'Folder moved',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useUpdateFolderMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateFolderRequest) => {
      const response = await withErrorHandling(api.v1.updateFolderV1DocumentFoldersPatch(payload), {
        loading: 'Updating folder...',
        success: 'Folder updated',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useMoveDocumentMutation = () => {
  return useMutation({
    mutationFn: async (payload: MoveDocumentRequest) => {
      const response = await withErrorHandling(
        api.v1.moveDocumentV1DocumentFoldersDocumentsMovePatch(payload),
        {
          loading: 'Moving document...',
          success: 'Document moved',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useDeleteFolderMutation = () => {
  return useMutation({
    mutationFn: async (payload: DeleteFolderV1DocumentFoldersFolderIdDeleteParams) => {
      const response = await withErrorHandling(
        api.v1.deleteFolderV1DocumentFoldersFolderIdDelete(payload),
        {
          loading: 'Archiving folder...',
          success: 'Folder archived',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useRestoreFolderMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      console.log('todo: not implemented', id);
      const response = await withErrorHandling(api.v1.deleteTaskV1TasksDelete({ id: 0 }), {
        loading: 'Restoring folder...',
        success: 'Folder restored',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useCreatePublicFolderLinkMutation = ({
  onSuccess,
}: {
  onSuccess?: (public_folder_id?: string) => void;
} = {}) => {
  return useMutation({
    mutationFn: async (
      payload: CreatePublicFolderLinkV1DocumentFoldersFolderIdPublicLinkPostParams,
    ) => {
      const response = await withErrorHandling(
        api.v1.createPublicFolderLinkV1DocumentFoldersFolderIdPublicLinkPost(payload),
        {
          loading: 'Creating public folder link...',
          success: 'Public folder link created',
        },
      );
      return response.data;
    },
    onSuccess: data => {
      onSuccess && onSuccess(data?.folder?.public_folder_id);
      queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useDeletePublicFolderLinkMutation = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: async (
      payload: DeletePublicFolderLinkV1DocumentFoldersFolderIdPublicLinkDeleteParams,
    ) => {
      const response = await withErrorHandling(
        api.v1.deletePublicFolderLinkV1DocumentFoldersFolderIdPublicLinkDelete(payload),
        {
          loading: 'Deleting public folder link...',
          success: 'Public folder link deleted',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      onSuccess && onSuccess();
      queryClient.invalidateQueries([DOCUMENTS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([DOCUMENTS_TREE_QK]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
    },
  });
};

export const useGetPublicDocumentByIdQuery = (
  payload: GetPublicDocumentV1UserDocumentsPublicPublicDocumentIdGetParams,
  disabled = false,
) => {
  const businessId = useGetCurrentBusinessId();
  return useQuery({
    queryKey: [DOCUMENTS_LIST_QUERY_KEY, JSON.stringify(payload), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPublicDocumentV1UserDocumentsPublicPublicDocumentIdGet(
        payload,
      );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useGetPublicFolderTreeByIdQuery = (
  payload: GetPublicDocumentFolderTreeV1UserDocumentsFoldersPublicTreeGetParams,
  disabled = false,
) => {
  const businessId = useGetCurrentBusinessId();
  return useQuery({
    queryKey: [DOCUMENTS_TREE_QK, JSON.stringify(payload), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPublicDocumentFolderTreeV1UserDocumentsFoldersPublicTreeGet(
        payload,
      );
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useGetPublicDocumentsFromFoldersByIdQuery = (
  payload: GetPublicFolderDocumentsV1UserDocumentsPublicFoldersDocumentsGetParams,
  disabled = false,
) => {
  const businessId = useGetCurrentBusinessId();
  const { page, ...data } = payload;

  return useInfiniteQuery({
    queryKey: [DOCUMENTS_LIST_QUERY_KEY, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response =
        await api.v1.getPublicFolderDocumentsV1UserDocumentsPublicFoldersDocumentsGet({
          ...data,
          page: pageParam || page,
        });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};
